import React, { useEffect, useState } from 'react';

import { Icons } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';

import { useUserHistory } from '@hooks/useUserHistory';

import { auth, userHistory as userHistorySelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setCreatedAt,
	setOrganizationUser,
	setPage,
	setPageSize,
	setSort,
	setUserAction,
} from '@slices/userHistory';

import { setFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { EAdminPanelPageTitles } from '@enums/common';

import { getColumns, getPlatformOwnerColumns } from './columns';

import styles from './UserHistory.module.scss';

const UserHistory = () => {
	const [selectedUser, setSelectedUser] = useState<TSelectOption | null>(null);
	const [selectedAction, setSelectedAction] = useState<TSelectOption | null>(null);

	const {
		accessPermission: {
			platformOwnerAdministrator,
			assetOwnerOrganizationAdministrator,
			networkOperatorOrganizationAdministrator,
			assetConsumerOrganizationAdministrator,
		},
	} = useAppSelector(auth);

	const dispatch = useAppDispatch();
	const {
		page,
		pageSize,
		organizationUser,
		sortDirection,
		sortFieldName,
		createdAtFrom,
		createdAtTo,
		userAction,
	} = useAppSelector(userHistorySelector);

	const {
		organizationsUsersData,
		countOfRecords,
		userHistoryByFilter,
		isLoading,
		userActionsData,
	} = useUserHistory({
		isAssetOwnerOrganizationAdministrator: assetOwnerOrganizationAdministrator,
		isNetworkOperatorOrganizationAdministrator: networkOperatorOrganizationAdministrator,
		isAssetConsumerOrganizationAdministrator: assetConsumerOrganizationAdministrator,
		organizationUser,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		createdAtFrom,
		createdAtTo,
		userAction,
	});

	const handleSort = (sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	};

	const startActionDate = createdAtFrom ? new Date(createdAtFrom) : null;
	const endActionDate = createdAtTo ? new Date(createdAtTo) : null;

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const onChangeStartActionDate = (value: number | null) => {
		dispatch(setCreatedAt({
				createdAtFrom: value,
				createdAtTo: createdAtTo ? createdAtTo : null,
			},
		));
	};

	const onChangeEndActionDate = (value: number | null) => {
		dispatch(setCreatedAt({
				createdAtFrom: createdAtFrom ? createdAtFrom : null,
				createdAtTo: value,
			},
		));
	};

	const handleResetFilters = () => {
		dispatch(setCreatedAt({
				createdAtFrom: null,
				createdAtTo: null,
			},
		));

		filters?.forEach(({ setState }) => {
			setState(null);
		});
	};

	const columns = platformOwnerAdministrator ? getPlatformOwnerColumns() : getColumns();

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const dateFilters: Array<ITableDatepickerFilter> = [
		{
			name: 'created',
			label: 'Start action date',
			handleChange: onChangeStartActionDate,
			selected: startActionDate,
			maxDate: createdAtTo ? new Date(createdAtTo) : null,
		},
		{
			name: 'updated',
			label: 'End action date',
			handleChange: onChangeEndActionDate,
			selected: endActionDate,
			minDate: createdAtFrom ? new Date(createdAtFrom) : null,
		},
	];

	const platformOwnerFilters: Array<TTableFilter> = [
		{
			name: 'type',
			placeholder: 'Action',
			option: selectedAction,
			options: userActionsData,
			minWidth: '200px',
			setState: setSelectedAction,
			defaultValue: userAction,
		},
	];

	const filters: Array<TTableFilter> = [
		{
			name: 'userName',
			placeholder: 'User name',
			option: selectedUser,
			options: organizationsUsersData,
			minWidth: '200px',
			setState: setSelectedUser,
			defaultValue: organizationUser,
		},
		...platformOwnerFilters,
	];

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedUser,
			currentValue: organizationUser,
			setAction: setOrganizationUser,
			fieldName: 'organizationUser',
			dispatch,
		});
	}, [dispatch, organizationUser, selectedUser]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedAction,
			currentValue: userAction,
			setAction: setUserAction,
			fieldName: 'userAction',
			dispatch,
		});
	}, [dispatch, selectedAction, userAction]);

	return (
		<div className={styles.userHistoryContainer}>
			<PageTitle
				icon={Icons.history({ isTitle: true })}
				title={EAdminPanelPageTitles.UserHistory}
			/>
			<div className={styles.userHistoryTableContainer}>
				<Table
					className={styles.maxWidth}
					columns={columns}
					data={userHistoryByFilter}
					dateFilters={dateFilters}
					filters={platformOwnerAdministrator
						? platformOwnerFilters
						: (assetOwnerOrganizationAdministrator
							|| networkOperatorOrganizationAdministrator
							|| assetConsumerOrganizationAdministrator)
							? filters
							: []
					}
					handleResetFilters={handleResetFilters}
					isLoading={isLoading}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default UserHistory;
